import { motion } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Seo } from "../seo";
import { LogoImage } from "../components/Logo";
import { PHONE_TEL, PHONE_DISPLAY, AREAS } from "../site";

const VALUES = [
  {
    title: "Price before dispatch",
    body: "You hear the exact price on the phone before a van moves. If the job turns out different on-site, we talk before we touch anything.",
  },
  {
    title: "Damage-free first",
    body: "Our default is always the non-destructive method — picks, wedges and decoders before drills. Your property stays intact.",
  },
  {
    title: "Built for Winnipeg",
    body: "Frozen locks, -40° nights, parkades and perimeter highways. This city is hard on locks, and we've built the service around exactly that.",
  },
];

export default function AboutPage() {
  return (
    <main className="pt-28 md:pt-36 pb-24">
      <Seo
        title="About Arrow Locksmith | Mobile Locksmith in Winnipeg, MB"
        description="Arrow Locksmith is a locally owned mobile automotive and residential locksmith serving Winnipeg, Manitoba — 24/7 lockouts, key fobs, rekeys and more. Call (204) 963-9081."
        path="/about"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="max-w-3xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 mb-4">About us</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.02] text-zinc-950">
            A Winnipeg locksmith that actually answers.
          </h1>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-zinc-600">
            Arrow Locksmith is a locally owned, fully mobile automotive and
            residential locksmith serving Winnipeg and surrounding communities.
            No storefront, no waiting room — the workshop is in the van, and the
            van comes to you. That's how we keep prices below the dealership and
            response times measured in minutes.
          </p>
          <p className="mt-4 text-base leading-relaxed text-zinc-600">
            We handle the calls nobody plans for: keys locked in the car at The
            Forks, a deadbolt that won't turn at midnight, a fob that quits the
            night before a road trip. One call to{" "}
            <a href={PHONE_TEL} className="text-brand font-semibold">{PHONE_DISPLAY}</a>{" "}
            reaches a real locksmith, 24 hours a day, 7 days a week.
          </p>
        </motion.div>

        <div className="mt-16 grid md:grid-cols-3 gap-6 md:gap-8">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ type: "spring", stiffness: 100, damping: 20, delay: i * 0.08 }}
              className="rounded-3xl bg-white border border-zinc-200 p-8"
            >
              <h2 className="font-heading text-xl font-semibold tracking-tight text-zinc-950">{v.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-zinc-600">{v.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 rounded-3xl bg-zinc-100 border border-zinc-200 p-8 md:p-12 flex flex-wrap items-center justify-between gap-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-zinc-950">
              Serving all of Winnipeg
            </h2>
            <p className="mt-3 text-sm leading-loose text-zinc-600 max-w-2xl">{AREAS.join(" · ")}</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <a
              href={PHONE_TEL}
              data-testid="about-call-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-alert px-7 py-4 text-base font-semibold text-white hover:bg-red-600 transition-colors duration-200"
            >
              <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
            </a>
            <Link
              to="/contact"
              data-testid="about-contact-link"
              className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white px-7 py-4 text-base font-semibold text-zinc-950 hover:border-zinc-950 transition-colors duration-200"
            >
              Contact us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
